import { z } from 'zod';

// =============================================================================
// SHARED ENUMS
// =============================================================================

export const PacingEnum = z.enum([
  'Slow & Reflective',
  'Moderate & Developing',
  'Fast & Action-Packed',
]);

// =============================================================================
// CHAPTER GOALS
// =============================================================================

export const ChapterGoalsSchema = z.object({
  plotDevelopment: z.string().describe(
    'How this chapter moves the plot forward. Reference specific events, revelations, or plot points. 1-2 sentences.'
  ),
  charDevelopment: z.string().describe(
    'How this chapter develops characters and relationships. Identify character growth, emotional moments, or new dynamics. 1-2 sentences.'
  ),
  stakesAndTension: z.string().describe(
    'How this chapter escalates tension or raises the stakes. Identify conflicts introduced, threats, or complications. 1-2 sentences.'
  ),
});

// =============================================================================
// CORE SCHEMAS (shared foundations)
// =============================================================================

/**
 * Core chapter fields shared by all chapter schemas (ideation, import, update).
 * keyEvents: neutral summary of key events — no dialogue, no sensory details, objective style.
 */
export const CoreChapterSchema = z.object({
  title: z.string().describe('A descriptive title for the chapter'),
  keyEvents: z.string().describe(
    'A neutral summary of the key events in this chapter. Use objective style — no dialogue, no sensory details. Target ~1 paragraph per 1000 projected words.'
  ),
  chapterGoals: ChapterGoalsSchema.describe('The narrative goals this chapter aims to accomplish'),
  pacing: PacingEnum.describe('The pacing of the chapter'),
});

/**
 * Core scene fields shared by all scene schemas (ideation, import, update).
 */
export const CoreSceneSchema = z.object({
  sceneNumber: z.number().describe('The chronological order of the scene within the chapter, starting from 1.'),
  title: z.string().describe('A descriptive title for the scene'),
  keyBeats: z.string().describe('The key story beats and moments that occur in this scene'),
});

// =============================================================================
// IDEATION SCHEMAS (AI Outputs — Planning)
// =============================================================================

/**
 * Schema for AI-generated chapter ideas
 */
export const ChapterIdeaSchema = CoreChapterSchema.extend({
  povCharacterName: z.string().optional().describe('The name of the character that should be the POV in this chapter'),
  povJustification: z.string().optional().describe('A brief explanation of why this character is the POV in this chapter'),
  projectedWordCount: z.number().describe('The projected word count of the chapter'),
});

/**
 * Schema for AI-generated single-chapter story ideas.
 * keyEvents: denser than long-form — target ~1 paragraph per 500 words.
 */
export const SingleChapterStoryIdeaSchema = CoreChapterSchema.extend({
  title: z.string().describe('A descriptive title for the story'),
  keyEvents: z.string().describe(
    'A neutral summary of the key events in this story. Use objective style — no dialogue, no sensory details. Target ~1 paragraph per 500 projected words.'
  ),
  chapterGoals: ChapterGoalsSchema.describe('The narrative goals this story aims to accomplish'),
  pacing: PacingEnum.describe('The pacing of the story'),
  povCharacterName: z.string().optional().describe('The name of the character that should be the POV in this chapter'),
  povJustification: z.string().optional().describe('A brief explanation of why this character is the POV in this chapter'),
  projectedWordCount: z.number().describe('The projected word count of the story'),
});

// =============================================================================
// IMPORT SCHEMAS (AI Outputs — Extraction from existing text)
// =============================================================================

/**
 * Schema for AI-imported scenes.
 * Used when extracting scenes from existing text (no projectedWordCount needed).
 */
export const SceneImportSchema = CoreSceneSchema;

/**
 * Schema for AI-imported chapters.
 * Similar to ChapterContext, but without the content to save tokens.
 */
export const ChapterImportSchema = CoreChapterSchema.extend({
  number: z.number().describe("The chapter number."),
  chapterHeading: z.string().describe("The exact chapter heading as it appears in the text, e.g. 'Chapter 36' or 'CHAPTER THIRTY-SIX' or 'Prologue'. Include only the heading line, not the title if it appears on a separate line."),
  povCharacterName: z.string().nullable().optional().describe("The name of the character whose point of view (POV) this chapter is written from. Use null for omniscient narration."),
  keyEvents: z.string().describe(
    'A neutral summary of the key events in this chapter. Use objective style — no dialogue, no sensory details. Target ~1 paragraph per 1000 words of chapter content.'
  ),
  firstLine: z.string().describe("The first line of the chapter's content. Should not include the chapter number or title."),
  lastLine: z.string().optional().describe("The last line of the chapter's content. Optional — only provide if confident this is the true final line of the chapter, not a scene-ending line before a divider."),
  scenes: z.array(SceneImportSchema).describe("The scenes within this chapter, in chronological order."),
});

/**
 * Schema for AI-generated scene ideas
 */
export const SceneIdeaSchema = CoreSceneSchema.extend({
  projectedWordCount: z.number().describe('The projected word count for this scene'),
});

// =============================================================================
// BOUNDARY SCHEMAS (AI Outputs — Boundary Detection)
// =============================================================================

/**
 * Schema for chapter boundary detection (step 1 of human-gated extraction).
 * Contains only the data needed to locate chapter boundaries in text —
 * no metadata like keyEvents, chapterGoals, scenes, or pacing.
 */
export const ChapterBoundarySchema = z.object({
  number: z.number().describe('The chapter number.'),
  chapterHeading: z.string().describe("The exact chapter heading as it appears in the text, e.g. 'Chapter 36' or 'CHAPTER THIRTY-SIX' or 'Prologue'. Include only the heading line, not the title if it appears on a separate line."),
  title: z.string().describe('A descriptive title for the chapter.'),
  firstLine: z.string().describe("The first line of the chapter's content. Should not include the chapter number or title."),
  lastLine: z.string().optional().describe("The last line of the chapter's content. Optional — only provide if confident this is the true final line."),
});

// =============================================================================
// TYPE INFERENCE
// =============================================================================

export type ChapterGoals = z.infer<typeof ChapterGoalsSchema>;
export type CoreChapter = z.infer<typeof CoreChapterSchema>;
export type CoreScene = z.infer<typeof CoreSceneSchema>;
export type ChapterIdea = z.infer<typeof ChapterIdeaSchema>;
export type ChapterImport = z.infer<typeof ChapterImportSchema>;
export type ChapterBoundary = z.infer<typeof ChapterBoundarySchema>;
export type SceneIdea = z.infer<typeof SceneIdeaSchema>;
export type SceneImport = z.infer<typeof SceneImportSchema>;
